import { ShoppingCart } from 'lucide-react';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatBRL, formatDate } from '@/lib/format';
import type { MlOrderRow } from '@/hooks/useMlOrders';

interface Props {
  orders?: MlOrderRow[];
}

const STATUS_LABEL: Record<string, string> = {
  paid: 'Pago',
  confirmed: 'Confirmado',
  payment_in_process: 'Pagamento em análise',
  payment_required: 'Aguardando pagamento',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
  invalid: 'Inválido',
};

function statusBadge(status: string) {
  const label = STATUS_LABEL[status] ?? status;
  const style =
    status === 'cancelled' || status === 'invalid'
      ? 'bg-negative/10 text-negative'
      : status === 'paid' || status === 'delivered' || status === 'confirmed'
        ? 'bg-positive/10 text-positive'
        : 'bg-surface-subtle text-ink-muted';
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${style}`}>{label}</span>;
}

export function OrdersTable({ orders = [] }: Props) {
  return (
    <div className="card p-5">
      <SectionHeader
        title="Pedidos do Mercado Livre"
        subtitle="Últimos 30 dias — sincronização automática a cada 5 min"
      />
      {orders.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="Nenhum pedido no período"
          description="Quando houver vendas no Mercado Livre, elas aparecerão aqui automaticamente."
        />
      ) : (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-muted uppercase tracking-wider border-b border-border">
                <th className="py-2 pr-4">Pedido</th>
                <th className="py-2 pr-4">Data</th>
                <th className="py-2 pr-4">Itens</th>
                <th className="py-2 pr-4">Comprador</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4 text-right">Total</th>
                <th className="py-2 text-right">Líquido</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {orders.slice(0, 20).map((o) => {
                const items = o.payload?.order_items ?? [];
                const itemsLabel =
                  items.length === 0
                    ? '—'
                    : items.length === 1
                      ? items[0]?.item?.title ?? '—'
                      : `${items[0]?.item?.title ?? '—'} +${items.length - 1}`;
                return (
                  <tr key={o.id}>
                    <td className="py-2.5 pr-4 font-mono text-xs text-ink-muted">#{o.ml_order_id}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">{formatDate(o.ordered_at)}</td>
                    <td className="py-2.5 pr-4 max-w-[260px] truncate" title={itemsLabel}>{itemsLabel}</td>
                    <td className="py-2.5 pr-4">{o.buyer_nickname ?? '—'}</td>
                    <td className="py-2.5 pr-4">{statusBadge(o.status)}</td>
                    <td className="py-2.5 pr-4 text-right font-medium">{formatBRL(o.total_amount_cents)}</td>
                    <td className="py-2.5 text-right text-ink-muted">{formatBRL(o.net_cents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {orders.length > 20 && (
            <p className="text-xs text-ink-muted mt-3">Mostrando 20 de {orders.length} pedidos do período.</p>
          )}
        </div>
      )}
    </div>
  );
}
